// Client-side JSON/CSV building + download for a single flagged thread, for offline/manual
// review. Kept free of React so it's trivially testable, same convention as
// livechat_mining/patternExport.js.

import { isBotAgent, isUnknownBotStatus } from './agentLabels';

// Keeps filenames filesystem-safe across OSes without pulling in a slugify dependency.
const sanitizeFilenamePart = (value) => {
  const slug = (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
  return slug || 'conversation';
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadFlaggedThreadJson = (thread, messages) => {
  const payload = {
    thread_id: thread.thread_id,
    email: thread.email,
    user_id: thread.user_id,
    start_at: thread.start_at,
    rating_comment: thread.rating_comment,
    flags: thread.flags,
    agents: thread.agents,
    messages,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  downloadBlob(blob, `flagged-chat-${sanitizeFilenamePart(thread.thread_id)}.json`);
};

// One row per flag (a thread can carry more than one) — thread-level columns repeat across
// rows, same flattening approach livechat_mining/patternExport.js uses for its metadata CSV.
// Conversation text is deliberately excluded here (that's what the JSON export is for).
// `metric` columns are the conversation-length metrics, which come back null for a brand whose
// warehouse doesn't carry them — see csvColumnsFor.
const CSV_COLUMNS = [
  { key: 'thread_id', label: 'Conversation ID' },
  { key: 'email', label: 'Email' },
  { key: 'user_id', label: 'User ID' },
  { key: 'start_at', label: 'Start at' },
  { key: 'flag_type', label: 'Flag type' },
  { key: 'evidence_quote', label: 'Evidence quote' },
  { key: 'reasoning', label: 'Reasoning' },
  { key: 'rating', label: 'Rating' },
  { key: 'rating_comment', label: 'Rating comment' },
  { key: 'checked', label: 'Checked' },
  { key: 'checked_by', label: 'Checked by' },
  { key: 'agents', label: 'Agent(s)' },
  { key: 'num_messages_all', label: 'Messages (all)', metric: true },
  { key: 'num_messages_agent', label: 'Messages (agent)', metric: true },
  { key: 'num_messages_agent_no_bot', label: 'Messages (agent, non-bot)', metric: true },
  { key: 'sum_num_words_all', label: 'Words (all)', metric: true },
  { key: 'total_duration', label: 'Duration (s)', metric: true },
];

// Drops the metric columns when no exported thread has any metrics, rather than shipping a
// block of always-empty columns that reads as "zero messages".
const csvColumnsFor = (threads) => CSV_COLUMNS.filter(
  (c) => !c.metric || threads.some((t) => t[c.key] != null),
);

// Same escaping as livechat_mining/patternExport.js: quote-wrap + double internal quotes for
// fields containing a comma/quote/newline, and guard against CSV injection by prefixing a
// leading apostrophe on fields a spreadsheet app would otherwise interpret as a formula.
const escapeCsvField = (value) => {
  let str = value == null ? '' : String(value);
  if (/^\s*[=+\-@]/.test(str)) str = `'${str}`;
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

// One CSV row string per flag on the thread (never zero rows, even for the — in practice
// never seen — case of a thread with no flags) — shared by the single-thread and bulk
// exports so both produce identical column layouts.
const threadCsvRows = (thread, columns) => {
  // Bot status is tri-state: an agent the brand's warehouse doesn't classify is exported as
  // "<id> (bot/human unknown)" rather than as a plain agent name, so a spreadsheet of Cashy
  // threads doesn't read as though every leg was handled by an identified human.
  const agents = (thread.agents || [])
    .map((a) => {
      if (isBotAgent(a)) return 'bot';
      return isUnknownBotStatus(a) ? `${a.agent_id} (bot/human unknown)` : a.agent_id;
    })
    .join('; ');
  const flags = thread.flags && thread.flags.length > 0 ? thread.flags : [{}];
  return flags.map((f) => {
    const row = {
      thread_id: thread.thread_id,
      email: thread.email,
      user_id: thread.user_id,
      start_at: thread.start_at,
      flag_type: f.flag_type,
      evidence_quote: f.evidence_quote,
      reasoning: f.reasoning,
      rating: thread.rating,
      rating_comment: thread.rating_comment,
      checked: thread.checked ? 'Yes' : 'No',
      checked_by: thread.checked ? thread.checked_by : '',
      agents,
      num_messages_all: thread.num_messages_all,
      num_messages_agent: thread.num_messages_agent,
      num_messages_agent_no_bot: thread.num_messages_agent_no_bot,
      sum_num_words_all: thread.sum_num_words_all,
      total_duration: thread.total_duration,
    };
    return columns.map((c) => escapeCsvField(row[c.key])).join(',');
  });
};

export const downloadFlaggedThreadCsv = (thread) => {
  const columns = csvColumnsFor([thread]);
  const header = columns.map((c) => escapeCsvField(c.label)).join(',');
  const blob = new Blob([[header, ...threadCsvRows(thread, columns)].join('\n')], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `flagged-chat-${sanitizeFilenamePart(thread.thread_id)}.csv`);
};

// Bulk export — every thread currently matching the report's filters, metadata-only (same
// one-row-per-flag layout as the single-thread CSV). Conversation text is excluded here too;
// use downloadFlaggedThreadsJson for that.
export const downloadFlaggedThreadsCsv = (threads) => {
  const columns = csvColumnsFor(threads);
  const header = columns.map((c) => escapeCsvField(c.label)).join(',');
  const rows = threads.flatMap((t) => threadCsvRows(t, columns));
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `flagged-chats-export-${threads.length}-conversations.csv`);
};

// Bulk export — every thread currently matching the report's filters, each with its full
// conversation transcript attached (already included on each thread by the export endpoint).
export const downloadFlaggedThreadsJson = (threads) => {
  const blob = new Blob([JSON.stringify(threads, null, 2)], { type: 'application/json' });
  downloadBlob(blob, `flagged-chats-export-${threads.length}-conversations.json`);
};
